import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import AnimatedBackground from '../components/common/AnimatedBackground';

const checkAnimation = keyframes`
  0% {
    stroke-dasharray: 0 100;
    stroke-dashoffset: 0;
  }
  100% {
    stroke-dasharray: 100 0;
    stroke-dashoffset: 0;
  }
`;

const pulseAnimation = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const Background = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #0a0f0a 0%, #1a2a1a 100%);
  position: relative;
  overflow: hidden;
`;

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  position: relative;
  z-index: 2;
`;

const SuccessCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 12px;
  padding: 50px 40px;
  width: 100%;
  max-width: 500px;
  text-align: center;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  backdrop-filter: none;
  animation: ${pulseAnimation} 3s ease-in-out infinite;

  @media (max-width: 768px) {
    padding: 40px 30px;
    border-radius: 15px;
  }
`;

const CheckmarkContainer = styled.div`
  width: 100px;
  height: 100px;
  margin: 0 auto 30px;
  position: relative;
`;

const CheckmarkCircle = styled.svg`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  stroke: #4caf50;
  stroke-width: 3;
  fill: none;
  stroke-dasharray: 314;
  stroke-dashoffset: 314;
  animation: ${checkAnimation} 1s ease-in-out forwards;
`;

const CheckmarkCheck = styled.svg`
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  stroke: #4caf50;
  stroke-width: 4;
  fill: none;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: 50;
  stroke-dashoffset: 50;
  animation: ${checkAnimation} 1s ease-in-out 0.5s forwards;
`;

const Title = styled.h1`
  color: #4caf50;
  font-size: 2.5rem;
  font-weight: bold;
  margin-bottom: 20px;
  text-transform: uppercase;
  font-family: 'Courier New', monospace;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Message = styled.p`
  color: #b0b0b0;
  font-size: 1.1rem;
  line-height: 1.6;
  margin-bottom: 30px;
`;

const ProgressContainer = styled.div`
  margin: 30px 0;
`;

const ProgressLabel = styled.div`
  color: #88cc88;
  font-size: 0.9rem;
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #45a049);
  border-radius: 4px;
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
  box-shadow: 0 0 10px rgba(76, 175, 80, 0.5);
`;

const Countdown = styled.span`
  color: #00d4ff;
  font-weight: bold;
`;

const InfoBox = styled.div`
  background: rgba(0, 212, 255, 0.1);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 8px;
  padding: 20px;
  margin-top: 20px;
`;

const InfoTitle = styled.h3`
  color: #00d4ff;
  margin: 0 0 10px 0;
  font-size: 1.1rem;
`;

const InfoText = styled.p`
  color: #b0b0b0;
  font-size: 0.9rem;
  margin: 5px 0;
  line-height: 1.4;
`;

const REDIRECT_SECONDS = 10;

const RefereeRegistrationSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(REDIRECT_SECONDS);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          navigate('/');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Прогресс обновляется каждые 100мс для плавной анимации
    const progressTimer = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) return 100;
        return prev + (100 / (REDIRECT_SECONDS * 10)); // 1% каждые 100мс при 10 секундах
      });
    }, 100);

    return () => {
      clearInterval(timer);
      clearInterval(progressTimer);
    };
  }, [navigate]);

  return (
    <Background>
      <AnimatedBackground />
      <Container>
        <SuccessCard>
          <CheckmarkContainer>
            <CheckmarkCircle viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="48" />
            </CheckmarkCircle>
            <CheckmarkCheck viewBox="0 0 100 100">
              <path d="M30 50 L45 65 L70 35" />
            </CheckmarkCheck>
          </CheckmarkContainer>

          <Title>Registration Complete!</Title>
          
          <Message>
            Your referee account has been successfully created. You are now registered 
            and ready to start officiating games.
          </Message>

          <ProgressContainer>
            <ProgressLabel>
              <span>Redirecting to homepage...</span>
              <Countdown>{countdown}s</Countdown>
            </ProgressLabel>
            <ProgressBar>
              <ProgressFill progress={progress} />
            </ProgressBar>
          </ProgressContainer>

          <InfoBox>
            <InfoTitle>🎯 Next Steps</InfoTitle>
            <InfoText>• Check your email for additional information</InfoText>
            <InfoText>• You can now log in to your referee account</InfoText>
            <InfoText>• The field will contact you with scheduling details</InfoText>
            <InfoText>• Welcome to the NovaWarfare referee team!</InfoText>
          </InfoBox>
        </SuccessCard>
      </Container>
    </Background>
  );
};

export default RefereeRegistrationSuccessPage;